import React from "react";
import { useI18n } from "../i18n";

/**
 * fruit and salt are judged by sweetness and coldness; gourmand (desserts,
 * pastries, creams) is judged by sweetness and hardness instead — a rich
 * dessert juice isn't "cold," it's heavy or light. That's what a Tunisian
 * shop actually means by these three types, so the sliders shown follow the
 * same split rather than asking for a rating that doesn't apply.
 */
const JUICE_TYPES = ["fruit", "gourmand", "salt"];
const JUICE_ATTRIBUTES = {
  fruit: ["sweetness", "coldness"],
  gourmand: ["sweetness", "hardness"],
  salt: ["sweetness", "coldness"],
};

const inputClass =
  "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";

export const DEFAULT_JUICE_DETAILS = {
  juice_type: "fruit",
  sweetness_level: 3,
  coldness_level: 1,
  hardness_level: 2,
  pg_ratio: 40,
  vg_ratio: 60,
  bottle_size_ml: 30,
  available_nicotine_levels: [0, 3, 6],
};

/**
 * A saved product only carries the attributes its type is rated on — the
 * other one is null. Fill it back in with a neutral default so its slider has
 * a sane starting point if the owner switches type.
 */
export const parseJuiceDetails = (raw) => {
  const details = typeof raw === "string" ? JSON.parse(raw) : raw;

  return {
    ...details,
    sweetness_level: details.sweetness_level ?? 3,
    coldness_level: details.coldness_level ?? 1,
    hardness_level: details.hardness_level ?? 2,
  };
};

/**
 * Only the attributes this juice type is actually rated on, so a gourmand
 * doesn't carry a leftover coldness value from before the type was switched.
 */
export const serializeJuiceDetails = (value) => {
  const attributes = JUICE_ATTRIBUTES[value.juice_type] || [];

  return {
    ...value,
    sweetness_level: attributes.includes("sweetness")
      ? value.sweetness_level
      : null,
    coldness_level: attributes.includes("coldness")
      ? value.coldness_level
      : null,
    hardness_level: attributes.includes("hardness")
      ? value.hardness_level
      : null,
  };
};

const Slider = ({ label, value, onChange }) => (
  <div>
    <label className={labelClass}>{label}</label>
    <input
      type="range"
      min="0"
      max="5"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full"
    />
  </div>
);

/** The juice-specific fields, shared by the product form and the range form. */
const JuiceDetailsFields = ({ value, onChange }) => {
  const { t } = useI18n();
  const attributes = JUICE_ATTRIBUTES[value.juice_type] || [];
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="border-t pt-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {t("products.form.juiceDetails")}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>{t("products.form.juiceType")}</label>
          <select
            value={value.juice_type}
            onChange={(e) => set({ juice_type: e.target.value })}
            className={inputClass}
          >
            {JUICE_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`products.form.juiceTypes.${type}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>{t("products.form.bottleSize")}</label>
          <input
            type="number"
            value={value.bottle_size_ml}
            onChange={(e) => set({ bottle_size_ml: parseInt(e.target.value) })}
            className={inputClass}
          />
        </div>

        {attributes.includes("sweetness") && (
          <Slider
            label={t("products.form.sweetness", { value: value.sweetness_level })}
            value={value.sweetness_level}
            onChange={(sweetness_level) => set({ sweetness_level })}
          />
        )}

        {attributes.includes("coldness") && (
          <Slider
            label={t("products.form.coldness", { value: value.coldness_level })}
            value={value.coldness_level}
            onChange={(coldness_level) => set({ coldness_level })}
          />
        )}

        {attributes.includes("hardness") && (
          <Slider
            label={t("products.form.hardness", { value: value.hardness_level })}
            value={value.hardness_level}
            onChange={(hardness_level) => set({ hardness_level })}
          />
        )}

        <div>
          <label className={labelClass}>{t("products.form.pg")}</label>
          <input
            type="number"
            value={value.pg_ratio}
            onChange={(e) => set({ pg_ratio: parseInt(e.target.value) })}
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>{t("products.form.vg")}</label>
          <input
            type="number"
            value={value.vg_ratio}
            onChange={(e) => set({ vg_ratio: parseInt(e.target.value) })}
            className={inputClass}
          />
        </div>
      </div>
    </div>
  );
};

export default JuiceDetailsFields;
